import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Building, MapPin, Shield, CheckCircle } from 'lucide-react';

const LegalPage = () => {
  const companyDetails = [
    { label: "Company Name", value: "Shri Vishnu Udhyog" },
    { label: "GSTIN", value: "09EALPG7629N1ZW", highlight: true },
    { label: "State", value: "Uttar Pradesh" },
  ];

  const addressDetails = {
    building: "Basement, Bhukand No.-7",
    street: "Transport Nagar",
    city: "Bulandshahr",
    district: "Bulandshahr",
    state: "Uttar Pradesh",
    pincode: "203001"
  };

  return (
    <div data-testid="legal-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-600 to-orange-800 text-white py-24" data-testid="legal-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/20 mb-6">
            <FileText className="w-10 h-10" />
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-6">
            Legal Information
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Complete transparency in our business operations. 
            View our official company information.
          </p>
        </div>
      </section>

      {/* Company Information */}
      <section className="py-20 bg-white" data-testid="company-info">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Company Details Card */}
            <div className="bg-gray-50 rounded-2xl p-8" data-testid="company-details">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center">
                  <Building className="w-7 h-7 text-brand-orange" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold">Company Details</h2>
                  <p className="text-gray-500">Official registration information</p>
                </div>
              </div>

              <div className="space-y-4">
                {companyDetails.map((detail, index) => (
                  <div 
                    key={index}
                    className="flex justify-between items-center py-3 border-b border-gray-200 last:border-0"
                  >
                    <span className="text-gray-600">{detail.label}</span>
                    <span className={`font-semibold ${detail.highlight ? 'text-brand-orange font-mono text-lg' : 'text-gray-900'}`}>
                      {detail.value}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-semibold text-green-800">GST Registered</p>
                    <p className="text-sm text-green-600">GSTIN: 09EALPG7629N1ZW</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Address Card */}
            <div className="bg-gray-50 rounded-2xl p-8" data-testid="address-details">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center">
                  <MapPin className="w-7 h-7 text-brand-orange" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold">Registered Address</h2>
                  <p className="text-gray-500">Principal place of business</p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <address className="not-italic text-gray-700 leading-relaxed">
                  <p className="font-semibold text-lg text-gray-900 mb-4">
                    Shri Vishnu Udhyog
                  </p>
                  <p>{addressDetails.building}</p>
                  <p>{addressDetails.street}</p>
                  <p>{addressDetails.city}, {addressDetails.district}</p>
                  <p className="font-semibold">{addressDetails.state} - {addressDetails.pincode}</p>
                  <p className="mt-4 text-brand-orange font-semibold">India</p>
                </address>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GST Certificate Section */}
      <section className="py-20 bg-gray-50" data-testid="gst-certificates">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">GST Registration</h2>
            <p className="section-subtitle">
              Official government-issued registration details
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h3 className="text-2xl font-semibold mb-4">GST Registered</h3>
            <p className="text-gray-600 max-w-md mx-auto mb-6">
              Shri Vishnu Udhyog is a GST registered entity operating in compliance 
              with all government regulations.
            </p>
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-6 py-3 rounded-full text-lg font-mono font-semibold">
              GSTIN: 09EALPG7629N1ZW
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-20 bg-gray-900 text-white" data-testid="compliance-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl font-bold mb-4">Our Compliance Standards</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              We are committed to operating with full transparency and compliance
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-800 p-8 rounded-xl text-center">
              <Shield className="w-12 h-12 mx-auto text-orange-400 mb-4" />
              <h3 className="text-xl font-semibold mb-3">GST Compliant</h3>
              <p className="text-gray-400 text-sm">
                Fully registered under GST with GSTIN: 09EALPG7629N1ZW
              </p>
            </div>
            <div className="bg-gray-800 p-8 rounded-xl text-center">
              <Building className="w-12 h-12 mx-auto text-orange-400 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Registered Company</h3>
              <p className="text-gray-400 text-sm">
                Operating as a registered business under Indian law
              </p>
            </div>
            <div className="bg-gray-800 p-8 rounded-xl text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-orange-400 mb-4" />
              <h3 className="text-xl font-semibold mb-3">Food Safety</h3>
              <p className="text-gray-400 text-sm">
                Adherence to FSSAI guidelines for food products
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-orange" data-testid="legal-cta">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center text-white">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">
            Have Questions About Our Business?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            We believe in complete transparency. Contact us for any queries.
          </p>
          <Link to="/contact" className="btn-outline" data-testid="contact-btn">
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LegalPage;
